import { useEffect, useState, useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import TeamLogo from "@/components/TeamLogo";
import TacticalBoard from "@/components/TacticalBoard";
import FinancialsPanel from "@/components/FinancialsPanel";
import StructuresPanel from "@/pages/team-detail/StructuresPanel";
import TeamLedgerPanel from "@/components/TeamLedgerPanel";
import OpLoansPanel from "@/components/OpLoansPanel";
import UpcomingMatches from "@/components/UpcomingMatches";
import TeamLeaderboards from "@/components/TeamLeaderboards";
import LoanRecalls from "@/components/LoanRecalls";
import TeamRecentResults from "@/components/TeamRecentResults";
import TeamDisciplinary from "@/components/TeamDisciplinary";
import LoanOptionDecisions from "@/components/LoanOptionDecisions";
import TrophyCabinet from "@/components/TrophyCabinet";
import { useAuth } from "@/context/AuthContext";
import { useAccentFn } from "@/lib/color";
import { Send } from "lucide-react";
import { StatBox, Vg11Box, RosterCountsBox, formatMln } from "./team-detail/helpers";
import { RosterSection } from "./team-detail/roster";
import { AdminAddPlayer } from "./team-detail/admin";
import { MakeOfferModal, ResolveLoanModal } from "./team-detail/modals";

export { MakeOfferModal } from "./team-detail/modals";

// Feature flag: interfaccia Prestiti Banca OP nascosta finché non viene attivata.
// Backend attivo; mettere a true per mostrare il pannello nella pagina squadra.
const OP_LOANS_ENABLED = false;

/**
 * Public team detail page: shows the full imported roster for a team.
 * Anyone can view any team's roster. The page is route-driven, so future
 * "my team" link from the navbar can just go to `/squadre/{my_team_id}`.
 */
export default function TeamDetail() {
    const { teamId } = useParams();
    const { user } = useAuth();
    const acc = useAccentFn();
    const [data, setData] = useState(null);
    const [division, setDivision] = useState(null);
    const [err, setErr] = useState("");
    const [offerTarget, setOfferTarget] = useState(null); // {entry_id, name, ...}
    const [resolveTarget, setResolveTarget] = useState(null); // {entry_id, name, playing_team_name}
    const [reloadKey, setReloadKey] = useState(0);
    const [marketOpen, setMarketOpen] = useState(false);
    const [summerOpen, setSummerOpen] = useState(false);
    const [currentSeason, setCurrentSeason] = useState(null);
    const [pendingUpdates, setPendingUpdates] = useState({}); // entry_id -> ["tm","pesdb"]
    const [pendingReload, setPendingReload] = useState(0);

    const isMyTeam = user?.team_id && user.team_id === teamId;
    const isAdmin = user?.role === "admin";
    const canManageUpdates = !!(isMyTeam || isAdmin);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/admin/settings/season`);
                if (!cancelled) setCurrentSeason(data?.current_season ?? null);
            } catch { /* ignore */ }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/market/windows/active`);
                if (!cancelled) {
                    setMarketOpen(Boolean(data));
                    setSummerOpen(data?.kind === "summer");
                }
            } catch { /* ignore */ }
        })();
        return () => { cancelled = true; };
    }, [reloadKey]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/teams/${teamId}/roster`);
                if (cancelled) return;
                setData(data);
                if (data?.team?.division_id) {
                    try {
                        const divs = await api.get("/divisions");
                        const d = (divs.data || []).find((x) => x.id === data.team.division_id);
                        if (d && !cancelled) setDivision(d);
                    } catch {/* ignore */}
                }
            } catch (e) {
                if (!cancelled) setErr(e.response?.data?.detail || e.message);
            }
        })();
        return () => { cancelled = true; };
    }, [teamId, reloadKey]);

    // Richieste di aggiornamento TM/PESDB pendenti (per la propria squadra o admin)
    useEffect(() => {
        if (!canManageUpdates) { setPendingUpdates({}); return; }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/teams/${teamId}/roster/update-requests`);
                if (cancelled) return;
                const map = {};
                for (const r of data?.pending || []) {
                    (map[r.entry_id] = map[r.entry_id] || []).push(r.kind);
                }
                setPendingUpdates(map);
            } catch { /* ignore */ }
        })();
        return () => { cancelled = true; };
    }, [teamId, canManageUpdates, pendingReload, reloadKey]);

    const { main, ugly, loans, loanedOut } = useMemo(() => {
        const main = [];
        const ugly = [];
        const loans = [];
        for (const p of data?.roster || []) {
            if (p.is_ugly) ugly.push(p);
            else if (p.is_loaned_out) loans.push(p);
            else main.push(p);
        }
        // Prestiti in-app (vivono in un'altra rosa) → mappa nella forma della sezione
        const away = (data?.away_loans || []).map((a) => ({
            ...a,
            is_loaned_out: true,
            is_away_loan: true,
            playing_team_name: a.destination_team_name,
        }));
        // Solo secco + diritto, esclusi gli obblighi (i legacy con kind null = secco)
        const legacyShown = loans.filter((p) => p.loan_kind !== "loan_obligation");
        const loanedOut = [...legacyShown, ...away];
        return { main, ugly, loans, loanedOut };
    }, [data]);

    // Deep-link da Vetrina: /squadre/{id}?offer={entry_id} apre il modale offerta.
    const [searchParams, setSearchParams] = useSearchParams();
    /* eslint-disable */
    useEffect(() => {
        if (!data) return;
        const offerEntryId = searchParams.get("offer");
        if (!offerEntryId) return;
        const canOffer = user && user.team_id && user.team_id !== teamId;
        if (canOffer) {
            const entry = (data.roster || []).find(
                (p) => p.entry_id === offerEntryId && !p.is_loaned_out
            );
            if (entry) setOfferTarget(entry);
        }
        const next = new URLSearchParams(searchParams);
        next.delete("offer");
        setSearchParams(next, { replace: true });
    }, [data]);
    /* eslint-enable */

    if (err) {
        return (
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="team-detail-error">
                <div className="border border-tff3b30 bg-tff3b30/10 text-tff3b30 text-sm p-4">
                    {err}
                </div>
                <Link to="/squadre" className="op-btn mt-4 inline-block">← Torna alle squadre</Link>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="team-detail-loading">
                <div className="op-label text-t8f95a3 animate-pulse">// CARICAMENTO ROSA…</div>
            </div>
        );
    }

    const t = data.team;
    const accent = division?.accent_color || "rgb(var(--tc-2bb6ff))";

    // Can the current viewer make a market offer for players in THIS team?
    // Only if: logged in, has own team, this team isn't their own.
    const canMakeOffer = !!(user && user.team_id && user.team_id !== teamId);

    // Gli 11 giocatori effettivamente usati nel calcolo del VG11:
    // top 11 per VG della rosa principale (esclusi Ugly e ceduti in prestito).
    const vg11Players = (data.roster || [])
        .filter((p) => !p.is_ugly && !p.is_loaned_out && p.vg != null)
        .sort((a, b) => (b.vg || 0) - (a.vg || 0))
        .slice(0, 11);

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="team-detail-page">
            <Link to="/squadre" className="op-label text-t8f95a3 hover:text-tffffff inline-block mb-6">
                ← Torna alle squadre
            </Link>

            {/* Hero card */}
            <div className="op-card p-5 sm:p-6 mb-8 border-l-4" style={{ borderLeftColor: accent }}>
                {/* Top row: team identity */}
                <div className="flex items-center gap-4 sm:gap-6 mb-5 sm:mb-6">
                    <TeamLogo team={t} accentColor={accent} size={72} />
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                            <div className="op-label" style={{ color: accent }}>
                                {division ? `${division.code} · ${division.name}` : "—"}
                            </div>
                            {isMyTeam && (
                                <span
                                    className="op-mono text-[9px] sm:text-[10px] uppercase px-2 py-0.5 border bg-t2bb6ff/10"
                                    style={{ borderColor: "rgb(var(--tc-2bb6ff))", color: "rgb(var(--tc-2bb6ff))" }}
                                    data-testid="my-team-badge"
                                >
                                    La tua squadra
                                </span>
                            )}
                        </div>
                        <h1
                            className="op-heading text-tffffff text-3xl sm:text-4xl lg:text-5xl truncate"
                            data-testid="team-detail-name"
                        >
                            {t.name}
                        </h1>
                        {t.manager_name && (
                            <p className="op-mono text-xs sm:text-sm text-tc9ccd3 mt-1">
                                Manager: <span className="text-tffffff">{t.manager_name}</span>
                            </p>
                        )}
                        {user && (
                            <PresidentTelegram
                                teamId={teamId}
                                handle={t.president_telegram}
                                isAdmin={isAdmin}
                                onSaved={() => setReloadKey((k) => k + 1)}
                            />
                        )}
                    </div>
                </div>

                {/* Bottom row: stats — responsive grid (2 → 3 → 8 columns) */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-8 gap-2 sm:gap-3">
                    <Vg11Box
                        value={data.vg11 != null ? data.vg11.toFixed(1) : "—"}
                        accent="rgb(var(--tc-2bb6ff))"
                        hint={`top ${data.vg11_size || 11}`}
                        players={vg11Players}
                        vg11={data.vg11}
                    />
                    <StatBox label="Budget" value={formatMln(t.budget)} accent="rgb(var(--tc-ffffff))" testId="team-budget" />
                    <StatBox label="Valore TM" value={formatMln(data.team_value)} accent="rgb(var(--tc-00bfff))" testId="team-value" />
                    <StatBox label="Legacy (importato)" value={formatMln(t.total_wages)} accent="rgb(var(--tc-5c6370))" testId="team-wages-old" />
                    <StatBox label="Stipendi applicati" value={formatMln(data.applied_wages_total)} accent="rgb(var(--tc-2bb6ff))" testId="team-wages-applied" />
                    <div className="col-span-2 sm:col-span-3 lg:col-span-2">
                        <RosterCountsBox
                            main={data.main_count}
                            ugly={data.ugly_count}
                            uglyCap={t.ugly_capacity ?? 5}
                            nfc={data.nfc_count ?? 0}
                            nfcMax={data.max_nfc_per_market ?? 20}
                            svi={data.svi_count ?? 0}
                            sviLabel={data.svi_label}
                            nfcLabel={data.nfc_label}
                            sviMax={data.max_svi_per_market ?? 10}
                            loans={data.loans_out_season ?? 0}
                            loansMax={data.max_loans_out ?? 6}
                        />
                    </div>
                    <StatBox
                        label="Stadio"
                        value={t.stadium_capacity ? `${(t.stadium_capacity / 1000).toFixed(0)}k` : "—"}
                        accent="rgb(var(--tc-e8ff00))"
                        testId="team-stadium"
                        hint={t.stadium_capacity ? `${t.stadium_capacity.toLocaleString("it-IT")} posti` : "Liv0 fondazione"}
                    />
                </div>
            </div>

            {/* Prossime partite da giocare (subito dopo l'identità squadra) */}
            <UpcomingMatches teamId={teamId} mine={!!isMyTeam} canManage={isMyTeam || isAdmin} />
            {/* Diritto di riscatto da decidere (solo la propria squadra) */}
            {isMyTeam && <div className="mt-6"><LoanOptionDecisions onResolved={() => setReloadKey((k) => k + 1)} /></div>}
            {/* Richiamo prestiti secchi (proprietario chiede, chi lo detiene accetta) */}
            {(isMyTeam || isAdmin) && <LoanRecalls teamId={teamId} onChanged={() => setReloadKey((k) => k + 1)} />}
            {/* Ultime partite giocate con risultati ed esito (forma) */}
            <TeamRecentResults teamId={teamId} limit={8} />
            {/* Classifiche interne: gol e migliori in campo della stagione */}
            <TeamLeaderboards teamId={teamId} />
            {/* Diffidati e squalificati della rosa */}
            <TeamDisciplinary teamId={teamId} />

            {/* Trophy Cabinet */}
            <TrophyCabinet
                teamId={teamId}
                managerName={t.manager_name}
                aliases={t.albo_aliases || []}
                isAdmin={isAdmin}
                onAliasesUpdated={(newAliases) => {
                    setData((prev) => prev ? { ...prev, team: { ...prev.team, albo_aliases: newAliases } } : prev);
                }}
            />

            {/* Tactical board: visible to everyone, editable only by the team's president (or admin) */}
            <div className="mb-10" data-testid="tactical-section">
                <TacticalBoard
                    teamId={teamId}
                    canEdit={isMyTeam || user?.role === "admin"}
                    roster={data.roster}
                    accent={accent}
                />
            </div>

            <FinancialsPanel teamId={teamId} accent={accent} reloadKey={reloadKey} canBuyStadium={isMyTeam || isAdmin} summerOpen={summerOpen} controlledAdmin={!!t?.controlled_admin} onStadiumUpgraded={() => setReloadKey((k) => k + 1)} />

            <StructuresPanel teamId={teamId} reloadKey={reloadKey} onChanged={() => setReloadKey((k) => k + 1)} />

            {OP_LOANS_ENABLED && (
                <OpLoansPanel
                    teamId={teamId}
                    isAdmin={isAdmin}
                    isMyTeam={isMyTeam}
                    reloadKey={reloadKey}
                    onChange={() => setReloadKey((k) => k + 1)}
                />
            )}

            <TeamLedgerPanel teamId={teamId} reloadKey={reloadKey} isAdmin={isAdmin} />

            {isAdmin && (
                <AdminAddPlayer teamId={teamId} teamName={t.name} onAdded={() => setReloadKey((k) => k + 1)} />
            )}

            <RosterSection title="Rosa principale" subtitle={`${main.length} giocatori`} players={main} accent={accent} testId="main-section" canMakeOffer={canMakeOffer} onMakeOffer={setOfferTarget} isAdmin={isAdmin} teamId={teamId} onLegacyWageSaved={() => setReloadKey((k) => k + 1)} canRenew={isMyTeam || isAdmin} onRenewed={() => setReloadKey((k) => k + 1)} buyerTeamId={canMakeOffer ? user.team_id : null} currentSeason={currentSeason} marketOpen={marketOpen} canListVetrina={isMyTeam} canRequestUpdate={canManageUpdates} pendingUpdates={pendingUpdates} onRequested={() => setPendingReload((k) => k + 1)} />
            {loanedOut.length > 0 && (
                <RosterSection
                    title="In prestito"
                    subtitle={`${loanedOut.length} giocatori dati in prestito (secco / diritto di riscatto)`}
                    players={loanedOut}
                    accent="rgb(var(--tc-8f95a3))"
                    testId="loans-section"
                    showLoanedTo
                    note="Giocatori che questa squadra ha dato in PRESTITO: giocano in un'altra squadra ma restano di proprietà. (Prestito con obbligo escluso: è di fatto una cessione.)"
                    isAdmin={isAdmin}
                    onResolveLoan={setResolveTarget}
                    teamId={teamId}
                    onLegacyWageSaved={() => setReloadKey((k) => k + 1)}
                />
            )}
            {ugly.length > 0 && (
                <RosterSection title="Settore Ugly" subtitle={`${ugly.length} giovani`} players={ugly} accent="rgb(var(--tc-ff6b00))" testId="ugly-section" showLoanedTo={false} canMakeOffer={canMakeOffer} onMakeOffer={setOfferTarget} isAdmin={isAdmin} teamId={teamId} onLegacyWageSaved={() => setReloadKey((k) => k + 1)} canRenew={isMyTeam || isAdmin} onRenewed={() => setReloadKey((k) => k + 1)} buyerTeamId={canMakeOffer ? user.team_id : null} currentSeason={currentSeason} marketOpen={marketOpen} canListVetrina={isMyTeam} canRequestUpdate={canManageUpdates} pendingUpdates={pendingUpdates} onRequested={() => setPendingReload((k) => k + 1)} />
            )}

            {data.roster.length === 0 && (
                <div className="op-card p-10 text-center">
                    <div className="op-label">Nessun giocatore ancora importato</div>
                    <p className="text-t8f95a3 mt-3 text-sm">
                        Lancia l&apos;import legacy dall&apos;Admin Panel per popolare la rosa.
                    </p>
                </div>
            )}

            {offerTarget && (
                <MakeOfferModal
                    target={offerTarget}
                    sellerTeam={t}
                    sellerTeamId={teamId}
                    buyerTeamId={user.team_id}
                    onClose={() => setOfferTarget(null)}
                    onDone={() => setOfferTarget(null)}
                />
            )}

            {resolveTarget && (
                <ResolveLoanModal
                    target={resolveTarget}
                    ownerTeamId={teamId}
                    ownerTeamName={t.name}
                    onClose={() => setResolveTarget(null)}
                    onDone={() => {
                        setResolveTarget(null);
                        setReloadKey((k) => k + 1);
                    }}
                />
            )}
        </div>
    );
}


function PresidentTelegram({ teamId, handle, isAdmin, onSaved }) {
    const [editing, setEditing] = useState(false);
    const [val, setVal] = useState(handle || "");
    const [busy, setBusy] = useState(false);
    const clean = (handle || "").replace(/^@/, "").trim();

    const save = async () => {
        setBusy(true);
        try {
            await api.put(`/teams/${teamId}/president-telegram`, { handle: val });
            setEditing(false);
            onSaved?.();
        } finally {
            setBusy(false);
        }
    };

    if (editing) {
        return (
            <div className="flex items-center gap-2 mt-1.5" data-testid="president-telegram-editor">
                <span className="op-mono text-xs text-t8f95a3">t.me/</span>
                <input
                    value={val}
                    onChange={(e) => setVal(e.target.value)}
                    placeholder="username"
                    className="op-mono text-xs bg-t0e1014 border border-t22252a px-2 py-1 text-tffffff focus:border-t2bb6ff outline-none w-40"
                    data-testid="president-telegram-input"
                />
                <button onClick={save} disabled={busy} className="op-mono text-[11px] px-2 py-1 border border-t2bb6ff/50 text-t2bb6ff hover:bg-t2bb6ff/10 disabled:opacity-40" data-testid="president-telegram-save">Salva</button>
                <button onClick={() => { setVal(handle || ""); setEditing(false); }} className="op-mono text-[11px] px-2 py-1 border border-t22252a text-t8f95a3 hover:border-t8f95a3" data-testid="president-telegram-cancel">Annulla</button>
            </div>
        );
    }

    return (
        <div className="flex items-center gap-2 mt-1.5">
            {clean ? (
                <a
                    href={`https://t.me/${clean}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 op-mono text-xs px-2 py-1 border border-t2bb6ff/40 text-t2bb6ff hover:bg-t2bb6ff/10 transition-colors"
                    data-testid="president-telegram-link"
                >
                    <Send className="w-3 h-3" /> Telegram presidente
                </a>
            ) : (
                isAdmin && <span className="op-mono text-[11px] text-t5c6370">Telegram presidente non impostato</span>
            )}
            {isAdmin && (
                <button onClick={() => setEditing(true)} className="op-mono text-[11px] text-t8f95a3 hover:text-tffffff underline" data-testid="president-telegram-edit">
                    {clean ? "modifica" : "imposta"}
                </button>
            )}
        </div>
    );
}
